import * as React from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { grey } from '@material-ui/core/colors';
import { isMobileDevice } from '@configuratorware/configurator-frontendgui/Framework/Helpers/isMobileDevice';
import { Card, CardContent, Grid, CardActions, DialogContent, DialogActions } from '@material-ui/core';
import { Icon } from '@material-ui/core';
import { Button } from '@material-ui/core';
import { Typography } from '@material-ui/core';
import { t, T } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import MainButton from '@configuratorware/configurator-frontendgui/Shared/Components/MainButton';
import AnalyticsWrapper from '@configuratorware/configurator-frontendgui/App/Shared/Components/Analytics/AnalyticsWrapper';
import customClassName from '@configuratorware/configurator-frontendgui/App/Utils/customClassName';
import { addToCart } from '../../Reducers/Configurator/Actions';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import CustomDialog from '@configuratorware/configurator-frontendgui/Shared/Components/CustomDialog';
import { subHeaderTan } from '../../ThemeProvider';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: 1,
    borderTop: `7px solid ${theme.palette.primary.main}`,
    padding: 15,
    overflow: 'visible',
    [theme.breakpoints.between('xs', 'lg')]: {
      marginTop: 16,
    },
    [theme.breakpoints.down('xs')]: {
      overflow: 'hidden',
      display: 'flex',
      boxShadow: 'none',
      '&:last-child': {
        padding: '12px 0 0',
      },
      paddingTop: 0,
      borderTop: `1px solid ${grey[500]}`,
      borderRadius: 0,
    },
  },

  cardUnboxed: {
    borderTop: 'none',
    boxShadow: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
    },
  },

  cardContentWrapper: {
    [theme.breakpoints.down('xs')]: {
      '&:last-child': {
        paddingBottom: 4,
      },
    },
  },

  AmountPriceContent: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 5,
    },
  },

  AmountPriceButton: {
    textTransform: 'none',
    whiteSpace: 'nowrap',
    margin: 0,
    padding: '6px 15px',
    color: theme.palette.primary.darkText,
    [theme.breakpoints.down('xs')]: {
      minWidth: 35,
      padding: 0,
    },
  },

  smallButtonSize: {
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
      lineHeight: 1,
    },
  },

  mainButton: {
    margin: 0,
    padding: '6px 15px',
    height: 40,
    lineHeight: 1.5,
    [theme.breakpoints.down('xs')]: {
      height: 56,
    },
    '&.Mui-disabled': {
      opacity: 0.6,
    },
  },

  mainButtonLabel: {
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('xs')]: {
      whiteSpace: 'normal',
      fontSize: 12,
    },
  },

  AmountPriceTypography: {
    textAlign: 'end',
    lineHeight: 1.2,
    fontSize: '1.5rem',
    [theme.breakpoints.down('xs')]: {
      padding: '3px 0',
      textAlign: 'start',
      fontSize: 12,
      lineHeight: 1,
    },
  },

  AmountPriceTypographyVat: {
    fontSize: 9,
    lineHeight: 1.2,
    textAlign: 'end',
    display: 'block',
    [theme.breakpoints.down('xs')]: {
      fontSize: 9,
      textAlign: 'start',
    },
  },

  cardActions: {
    padding: '8px 0',
    [theme.breakpoints.down('xs')]: {
      padding: 0,
    },
  },

  configurationButton: {
    fontWeight: 'bold',
    textTransform: 'none',
    padding: '6px 15px',
    marginRight: 5,
    '&:hover': {
      cursor: 'pointer',
    },
  },

  summary: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: 5,
    },
  },

  orderedNote: {
    color: `${subHeaderTan}`,
    paddingTop: '10px',
  },
  successDialog: {
    '& .MuiIconButton-root': { display: 'none' },
  },
}));

const CustomAmountPrice = ({
  boxed,
  priceFormatted,
  hidePrices,
  vatNotice,
  showReceiveOfferForm,
  showCallToAction,
  isCreator,
  width,
  openConfigurationOverview,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const isMobile = isMobileDevice() || width === 'xs';
  const boxStyle = boxed ? classes.card : classes.cardUnboxed;
  const query = new URLSearchParams(location.search);
  const orderId = useSelector((state) => state.configurator?.configuration?.customdata?.erpOrderId);
  const showOrderSuccess = useSelector((state) => state.configurator?.showOrderSuccess);
  const configurationType = useSelector((state) => state.configurator?.configuration?.configurationType);
  const isDisabled = !query.has('_ebootis_customer_id') || (configurationType === 'cart' && orderId);
  const onAddToCartClick = () => {
    Services.ui.toggle('globalLoader', true);
    dispatch(addToCart());
  };

  return (
    <>
      <Card className={clsx(boxStyle, customClassName('cta-box'))}>
        <Grid item xs={3} sm={12}>
          <CardContent className={classes.cardContentWrapper} classes={{ root: classes.AmountPriceContent }}>
            {isCreator && !isMobile && (
              <Grid container>
                <Grid item xs={12} sm={12} md={12} lg={12} className={classes.summary}>
                  <Icon>list_alt</Icon>
                  <Button
                    className={classes.configurationButton}
                    variant="text"
                    size="small"
                    onClick={openConfigurationOverview}
                  >
                    {t('amountPrice.configurationOverview')}
                  </Button>
                </Grid>
              </Grid>
            )}

            <Grid container>
              {orderId && (
                <Grid item xs={8}>
                  <Typography variant="body2" className={classes.orderedNote}>
                    {T('addToCart.orderedHint', { orderId })}
                  </Typography>
                </Grid>
              )}
              {!hidePrices && (
                <Grid item xs={orderId ? 4 : 12}>
                  <Typography
                    variant="body2"
                    classes={{
                      root: classes.AmountPriceTypography,
                    }}
                  >
                    <b>{priceFormatted}</b>
                  </Typography>

                  {priceFormatted && (
                    <Typography
                      className={classes.pos}
                      variant="caption"
                      classes={{
                        root: classes.AmountPriceTypographyVat,
                      }}
                    >
                      <i>{vatNotice}</i>
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Grid>

        <Grid item xs={9} sm={12}>
          <CardActions className={classes.cardActions}>
            {showCallToAction && (
              <Grid item xs={12}>
                <AnalyticsWrapper targetId="add_to_cart">
                  <MainButton
                    className={customClassName('cta-button')}
                    variant="contained"
                    fullWidth
                    size="medium"
                    onClick={onAddToCartClick}
                    disabled={isDisabled}
                    classes={{
                      root: classes.mainButton,
                      label: classes.mainButtonLabel,
                    }}
                  >
                    {t(showReceiveOfferForm ? 'receiveOfferForm.sendButton' : 'amountPrice.intoCart')}
                  </MainButton>
                </AnalyticsWrapper>
              </Grid>
            )}
          </CardActions>
        </Grid>
      </Card>
      {showOrderSuccess && (
        <CustomDialog
          open={showOrderSuccess}
          dialogTitle={t('addToCart.orderedSuccessTitle')}
          className={classes.successDialog}
        >
          <DialogContent>
            <Typography variant="body2">{T('addToCart.orderedNote', { orderId })}</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => {
                window.close();

                if (window.opener && !window.opener.closed) {
                  window.opener.focus();
                  window.close();
                  return;
                }
                window.location.replace('about:blank');
              }}
            >
              {t('addToCart.close')}
            </Button>
            <Button variant="contained" color="primary" onClick={() => window.location.reload()}>
              {t('addToCart.newConfiguration')}
            </Button>
          </DialogActions>
        </CustomDialog>
      )}
    </>
  );
};

export default CustomAmountPrice;
